import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, X, Flag } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';

const MAX_TAREAS = 8;

const PriorityFlag = ({ prioridad }) => {
  const cfg = {
    urgente: 'text-rose-500',
    alta:    'text-orange-500',
    media:   'text-slate-500',
    baja:    'text-slate-400',
  }[prioridad];
  if (!cfg) return <span className="w-2.5 h-2.5" />;
  return (
    <Flag
      size={11}
      strokeWidth={2}
      className={cfg}
      style={prioridad === 'baja' ? {} : { fill: 'currentColor' }}
    />
  );
};

const FechaChip = ({ fecha }) => {
  if (!fecha) return <span className="text-slate-300 text-xs">—</span>;
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(fecha + 'T00:00:00');
  const diff = Math.floor((d - today) / 86400000);
  let cls = 'text-slate-500';
  let label = d.toLocaleDateString('es-VE', { day: 'numeric', month: 'short' });
  if (diff < 0) cls = 'text-rose-600 font-medium';
  else if (diff <= 2) cls = 'text-amber-600 font-medium';
  if (diff === 0) label = 'Hoy';
  if (diff === 1) label = 'Mañana';
  if (diff === -1) label = 'Ayer';
  return <span className={`text-xs tabular-nums ${cls}`}>{label}</span>;
};

const TABS = [
  { key: 'hoy',      label: 'Hoy' },
  { key: 'semana',   label: 'Esta semana' },
  { key: 'vencidas', label: 'Vencidas' },
];

const MisTareasWidget = () => {
  const navigate = useNavigate();
  const [tareas, setTareas] = useState([]);
  const [proyectos, setProyectos] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('hoy');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const v = localStorage.getItem('widget_dismiss_mis_tareas');
    if (v === new Date().toDateString()) setDismissed(true);
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [tareasResp, proyectosResp] = await Promise.all([
          axiosInstance.get('/api/tareas', { params: { asignado: 'me' } }),
          axiosInstance.get('/api/proyectos'),
        ]);
        const colorBySlug = {};
        (proyectosResp.data || []).forEach((p) => { colorBySlug[p.slug] = p.color; });
        setProyectos(colorBySlug);
        setTareas(tareasResp.data || []);
      } catch (err) {
        console.error('Error widget mis tareas:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('widget_dismiss_mis_tareas', new Date().toDateString());
    setDismissed(true);
  };

  const counts = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    let hoy = 0, semana = 0, vencidas = 0;
    tareas.forEach((t) => {
      const isDone = ['hecho', 'cancelado'].includes(t.estado?.categoria);
      if (isDone) return;
      // En espera de una predecesora: su plazo no corre, no cuenta.
      if (t.congelada) return;
      if (!t.fecha_vencimiento) return;
      const d = new Date(t.fecha_vencimiento + 'T00:00:00');
      const diff = Math.floor((d - today) / 86400000);
      if (diff < 0) vencidas++;
      else if (diff === 0) hoy++;
      else if (diff <= 7) semana++;
    });
    return { hoy, semana, vencidas };
  }, [tareas]);

  // Auto-seleccionar tab con tareas (prioridad: vencidas > hoy > semana)
  useEffect(() => {
    if (loading) return;
    if (counts.vencidas > 0) setTab('vencidas');
    else if (counts.hoy > 0) setTab('hoy');
    else if (counts.semana > 0) setTab('semana');
  }, [loading]); // eslint-disable-line

  const filtradas = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    return tareas
      .filter((t) => {
        const isDone = ['hecho', 'cancelado'].includes(t.estado?.categoria);
        if (isDone) return false;
        if (t.congelada) return false;
        if (!t.fecha_vencimiento) return false;
        const d = new Date(t.fecha_vencimiento + 'T00:00:00');
        const diff = Math.floor((d - today) / 86400000);
        if (tab === 'hoy') return diff === 0;
        if (tab === 'semana') return diff >= 0 && diff <= 7;
        if (tab === 'vencidas') return diff < 0;
        return false;
      })
      .sort((a, b) => (a.fecha_vencimiento || '').localeCompare(b.fecha_vencimiento || ''));
  }, [tareas, tab]);

  const visibles = filtradas.slice(0, MAX_TAREAS);
  const restantes = Math.max(0, filtradas.length - MAX_TAREAS);

  if (loading) return null;

  const totalActivas = tareas.filter((t) => !['hecho', 'cancelado'].includes(t.estado?.categoria)).length;
  if (totalActivas === 0 || dismissed) return null;

  const handleClick = (t) => {
    navigate(`/proyectos/${t.proyecto_slug}/tareas/${t.numero}`);
  };

  return (
    <section className="relative bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden group">
      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 z-20 w-6 h-6 rounded-md text-slate-300 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
        title="Ocultar por hoy"
      >
        <X size={14} />
      </button>

      {/* Header */}
      <div className="flex items-baseline gap-2 px-5 pt-4 pb-3">
        <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
        <h3 className="text-sm font-semibold text-slate-900 tracking-tight">Mis tareas</h3>
        <span className="text-xs text-slate-400 tabular-nums">
          {totalActivas} {totalActivas === 1 ? 'activa' : 'activas'}
        </span>
        {counts.vencidas > 0 && (
          <>
            <span className="text-slate-200">·</span>
            <span className="text-xs font-medium text-rose-600 tabular-nums">
              {counts.vencidas} vencida{counts.vencidas === 1 ? '' : 's'}
            </span>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-4 pb-2">
        {TABS.map((t) => {
          const count = counts[t.key];
          const isActive = tab === t.key;
          const isVencidas = t.key === 'vencidas';
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              {t.label}
              <span className={`text-[10px] tabular-nums ${
                isActive
                  ? 'text-white/70'
                  : isVencidas && count > 0
                    ? 'text-rose-600 font-semibold'
                    : 'text-slate-400'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Lista */}
      <AnimatePresence mode="wait">
        {visibles.length === 0 ? (
          <motion.div
            key={`empty-${tab}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="px-5 py-8 text-center"
          >
            <Sparkles size={18} className="text-slate-300 mx-auto mb-1.5" strokeWidth={1.5} />
            <p className="text-xs text-slate-500">
              {tab === 'hoy' ? 'Nada vence hoy.' :
               tab === 'semana' ? 'Nada en la próxima semana.' :
               'Sin tareas vencidas.'}
            </p>
          </motion.div>
        ) : (
          <motion.ul
            key={`list-${tab}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="divide-y divide-slate-100 border-t border-slate-100"
          >
            {visibles.map((t) => {
              const color = proyectos[t.proyecto_slug] || '#06b6d4';
              const proyectoNombre = t.proyecto_nombre || t.proyecto_slug || '';
              return (
                <li key={t.id}>
                  <button
                    onClick={() => handleClick(t)}
                    className="w-full grid grid-cols-[minmax(0,1fr)_auto_auto_14px] items-center gap-3 px-5 py-2 hover:bg-slate-50/70 transition-colors text-left"
                  >
                    <span className="text-sm text-slate-800 truncate">{t.titulo}</span>
                    <span className="inline-flex items-center gap-1.5 flex-shrink-0">
                      <span
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-[11px] text-slate-500 truncate max-w-[160px]">
                        {proyectoNombre}
                      </span>
                    </span>
                    <FechaChip fecha={t.fecha_vencimiento} />
                    <span className="flex items-center justify-center">
                      <PriorityFlag prioridad={t.prioridad} />
                    </span>
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>

      {/* Footer */}
      <button
        onClick={() => navigate('/proyectos/mis-tareas')}
        className="w-full flex items-center justify-center gap-1.5 px-5 py-2.5 text-xs font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-50 border-t border-slate-100 transition-colors"
      >
        {restantes > 0 ? (
          <>Ver las {restantes} restante{restantes === 1 ? '' : 's'} y todas las demás</>
        ) : (
          <>Ver todas mis tareas</>
        )}
        <ArrowRight size={12} />
      </button>
    </section>
  );
};

export default MisTareasWidget;
